import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "../../config";
import "./WorkPlanning.css";

const partsInBerlin = (date) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  return Object.fromEntries(
    parts.filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
};

const berlinInput = (value) => {
  const text = String(value);
  const date = new Date(
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)
      ? text.replace(" ", "T") + "Z"
      : text
  );
  const p = partsInBerlin(date);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// Berliner Ortszeit unabhängig von der Zeitzone des Computers.
const berlinToUtc = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    throw new Error("Bitte Datum und Uhrzeit vollständig angeben.");
  }

  const naive = new Date(value + ":00Z");
  if (
    Number.isNaN(naive.getTime()) ||
    naive.toISOString().slice(0, 16) !== value
  ) {
    throw new Error("Ungültiges Datum.");
  }

  const offsets = new Set();

  for (const hours of [-36, -24, -12, 0, 12, 24, 36]) {
    const probe = new Date(naive.getTime() + hours * 3600000);
    const p = partsInBerlin(probe);
    const localAsUtc = Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour), Number(p.minute), Number(p.second)
    );
    offsets.add(localAsUtc - probe.getTime());
  }

  const candidates = [...offsets]
    .map((offset) => new Date(naive.getTime() - offset))
    .filter((date) => berlinInput(date.toISOString()) === value);

  if (candidates.length !== 1) {
    throw new Error(
      "Diese Berliner Uhrzeit ist wegen der Zeitumstellung nicht eindeutig oder existiert nicht. Bitte eine andere Uhrzeit wählen."
    );
  }

  return candidates[0].toISOString().replace(".000Z", "Z");
};

const shiftDay = (day, count) => {
  const date = new Date(day + "T12:00:00Z");
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
};

const initialMonday = () => {
  const p = partsInBerlin(new Date());
  const today = `${p.year}-${p.month}-${p.day}`;
  const weekday = new Date(today + "T12:00:00Z").getUTCDay();
  return shiftDay(today, -(weekday === 0 ? 6 : weekday - 1));
};

const dayLabel = (day) =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(new Date(day + "T12:00:00Z"));

const displayTime = (value) => {
  const input = berlinInput(value);
  const [day, time] = input.split("T");
  return `${day.slice(8, 10)}.${day.slice(5, 7)}. ${time}`;
};

const employeeName = (user) =>
  [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;

const assignmentName = (entry) =>
  [entry.user_first_name, entry.user_last_name]
    .filter(Boolean).join(" ") || entry.user_email;

const WorkPlanning = () => {
  const [week, setWeek] = useState(initialMonday);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [revision, setRevision] = useState(0);
  const busyRef = useRef(false);

  const token = localStorage.getItem("token");
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => shiftDay(week, index)),
    [week]
  );

  const request = async (path, options = {}) => {
    const response = await fetch(`${API_URL}/api/${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const data = await response.json().catch(() => null);

    if (!response.ok || data === null) {
      throw new Error(data?.error || "Die Anfrage konnte nicht verarbeitet werden.");
    }
    return data;
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    const load = async () => {
      try {
        const params = new URLSearchParams({
          start: berlinToUtc(week + "T00:00"),
          end: berlinToUtc(shiftDay(week, 7) + "T00:00"),
        });

        const get = async (path) => {
          const response = await fetch(`${API_URL}/api/${path}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          const data = await response.json().catch(() => null);
          if (!response.ok || !Array.isArray(data)) {
            throw new Error(data?.error || "Planungsdaten konnten nicht geladen werden.");
          }
          return data;
        };

        const [plans, employees, projects] = await Promise.all([
          get("work-assignments?" + params.toString()),
          get("users"),
          get("work-sites"),
        ]);

        if (!controller.signal.aborted) {
          setEntries(plans);
          setUsers(employees.filter((user) =>
            ["user", "admin"].includes(user.role)
          ));
          setSites(projects);
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [week, revision, token]);

  const activeSites = sites.filter(
    (site) =>
      Number(site.is_active) === 1 &&
      Number(site.customer_is_active) === 1
  );

  const openEditor = (day, entry = null) => {
    if (busyRef.current) return;
    setFormError("");
    setSuccess("");
    setEditor({ id: entry?.id ?? null });

    setForm({
      user_id: String(entry?.user_id ?? users[0]?.id ?? ""),
      site_id: String(entry?.site_id ?? activeSites[0]?.id ?? ""),
      planned_start: entry
        ? berlinInput(entry.planned_start)
        : day + "T08:00",
      planned_end: entry
        ? berlinInput(entry.planned_end)
        : day + "T16:00",
      planned_break_minutes: String(entry?.planned_break_minutes ?? 30),
      notes: entry?.notes || "",
    });
  };

  const change = (event) => {
    setForm((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;

    let body;
    try {
      body = {
        user_id: Number(form.user_id),
        site_id: Number(form.site_id),
        planned_start: berlinToUtc(form.planned_start),
        planned_end: berlinToUtc(form.planned_end),
        planned_break_minutes: Number(form.planned_break_minutes),
        notes: form.notes.trim(),
      };

      const duration =
        new Date(body.planned_end) - new Date(body.planned_start);

      if (
        !body.user_id || !body.site_id ||
        !Number.isInteger(body.planned_break_minutes) ||
        body.planned_break_minutes < 0 ||
        duration <= 0 || duration > 86400000 ||
        body.planned_break_minutes * 60000 >= duration
      ) {
        throw new Error(
          "Bitte Mitarbeiter, Projekt und gültige Zeiten angeben. Die Pause muss kürzer als der Einsatz sein."
        );
      }
    } catch (err) {
      setFormError(err.message);
      return;
    }

    busyRef.current = true;
    setSaving(true);
    setFormError("");
    setSuccess("");

    try {
      const result = await request(
        "work-assignments" + (editor.id ? "/" + editor.id : ""),
        {
          method: editor.id ? "PATCH" : "POST",
          body: JSON.stringify(body),
        }
      );
      if (result.success !== true) {
        throw new Error("Speicherung wurde nicht bestätigt.");
      }

      setEditor(null);
      setSuccess(
        result.changed === false
          ? "Der Einsatz war bereits unverändert gespeichert."
          : "Einsatz wurde gespeichert."
      );
      setLoading(true);
      setRevision((value) => value + 1);
    } catch (err) {
      setFormError(
        err instanceof TypeError
          ? "Keine Serverantwort erhalten. Bitte vor einem erneuten Versuch den Wochenplan aktualisieren und prüfen."
          : err.message
      );
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const cancel = async (entry) => {
    if (busyRef.current) return;
    const reason = window.prompt("Warum soll dieser Einsatz storniert werden?");
    if (reason === null) return;
    if (!reason.trim() || reason.trim().length > 1000) {
      setError("Bitte einen Stornogrund mit 1 bis 1000 Zeichen eingeben.");
      return;
    }

    busyRef.current = true;
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const result = await request(
        `work-assignments/${entry.id}/cancel`,
        {
          method: "POST",
          body: JSON.stringify({ reason: reason.trim() }),
        }
      );
      if (result.success !== true) {
        throw new Error("Stornierung wurde nicht bestätigt.");
      }
      setSuccess("Einsatz wurde storniert.");
      setLoading(true);
      setRevision((value) => value + 1);
    } catch (err) {
      setError(
        err instanceof TypeError
          ? "Keine Serverantwort erhalten. Bitte aktualisieren und den Einsatzstatus prüfen."
          : err.message
      );
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const moveWeek = (count) => {
    if (busyRef.current) return;
    setEditor(null);
    setSuccess("");
    setLoading(true);
    setWeek((previous) => shiftDay(previous, count));
  };

  return (
    <section className="work-planning">
      <div className="wp-heading">
        <div>
          <h2>Arbeitsplanung</h2>
          <p>
            {dayLabel(week)} bis {dayLabel(shiftDay(week, 6))}
            {" · "}Alle Zeiten in Europe/Berlin
          </p>
        </div>
        <div className="wp-actions">
          <button type="button" disabled={saving} onClick={() => moveWeek(-7)}>
            Vorherige Woche
          </button>
          <button type="button" disabled={saving} onClick={() => moveWeek(7)}>
            Nächste Woche
          </button>
          <button
            type="button"
            disabled={saving || loading}
            onClick={() => {
              setLoading(true);
              setRevision((value) => value + 1);
            }}
          >
            Aktualisieren
          </button>
        </div>
      </div>

      {success && <p className="wp-success" role="status">{success}</p>}
      {error && <p className="wp-error" role="alert">{error}</p>}

      {editor && (
        <form className="wp-editor" onSubmit={save}>
          <h3>{editor.id ? "Einsatz bearbeiten" : "Einsatz planen"}</h3>
          <fieldset disabled={saving}>
            <div className="wp-form-grid">
              <label>
                Mitarbeiter *
                <select
                  name="user_id"
                  value={form.user_id}
                  onChange={change}
                  disabled={Boolean(editor.id)}
                  required
                >
                  <option value="">Auswählen</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {employeeName(user)}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Projekt *
                <select
                  name="site_id"
                  value={form.site_id}
                  onChange={change}
                  required
                >
                  <option value="">Auswählen</option>
                  {activeSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.customer_name} · {site.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Geplanter Beginn *
                <input
                  type="datetime-local"
                  name="planned_start"
                  value={form.planned_start}
                  onChange={change}
                  required
                />
              </label>

              <label>
                Geplantes Ende *
                <input
                  type="datetime-local"
                  name="planned_end"
                  value={form.planned_end}
                  onChange={change}
                  required
                />
              </label>

              <label>
                Geplante Pause in Minuten
                <input
                  type="number"
                  name="planned_break_minutes"
                  value={form.planned_break_minutes}
                  onChange={change}
                  min={0}
                  max={1439}
                  step={1}
                  required
                />
              </label>

              <label>
                Hinweise
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={change}
                  maxLength={1000}
                  rows={3}
                />
              </label>
            </div>

            {formError && <p className="wp-error" role="alert">{formError}</p>}

            <div className="wp-actions">
              <button type="submit">
                {saving ? "Wird gespeichert …" : "Einsatz speichern"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!busyRef.current) setEditor(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </fieldset>
        </form>
      )}

      <label className="wp-cancelled-toggle">
        <input
          type="checkbox"
          checked={showCancelled}
          onChange={(event) => setShowCancelled(event.target.checked)}
        />
        Stornierte Einsätze anzeigen
      </label>

      {loading ? (
        <p>Wochenplan wird geladen …</p>
      ) : error ? null : (
        <>
          {!activeSites.length && (
            <p>Zum Planen benötigst du ein aktives Projekt mit aktivem Kunden.</p>
          )}

          <div className="wp-week">
            {days.map((day) => {
              const start = new Date(berlinToUtc(day + "T00:00"));
              const end = new Date(berlinToUtc(shiftDay(day, 1) + "T00:00"));

              const dayEntries = entries.filter((entry) => {
                const entryStart = new Date(
                  berlinToUtc(berlinInput(entry.planned_start))
                );
                const entryEnd = new Date(
                  berlinToUtc(berlinInput(entry.planned_end))
                );

                return (
                  (showCancelled || entry.status === "geplant") &&
                  entryStart < end && entryEnd > start
                );
              });

              return (
                <section className="wp-day" key={day}>
                  <h3>{dayLabel(day)}</h3>
                  <button
                    type="button"
                    disabled={saving || !users.length || !activeSites.length}
                    onClick={() => openEditor(day)}
                  >
                    Einsatz planen
                  </button>

                  {!dayEntries.length && <p className="wp-empty">Keine Einsätze</p>}

                  {dayEntries.map((entry) => (
                    <article
                      key={entry.id}
                      className={`wp-entry ${
                        entry.status === "storniert" ? "wp-entry-cancelled" : ""
                      }`}
                    >
                      <strong>{assignmentName(entry)}</strong>
                      <p>{entry.customer_name} · {entry.project_name}</p>
                      {entry.project_address && <p>{entry.project_address}</p>}
                      <p>
                        {displayTime(entry.planned_start)}
                        {" – "}
                        {displayTime(entry.planned_end)}
                      </p>
                      <p>Pause: {entry.planned_break_minutes} Minuten</p>
                      {entry.notes && <p className="wp-notes">{entry.notes}</p>}

                      {entry.status === "geplant" ? (
                        <div className="wp-actions">
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => openEditor(day, entry)}
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            className="wp-cancel-button"
                            disabled={saving}
                            onClick={() => cancel(entry)}
                          >
                            Stornieren
                          </button>
                        </div>
                      ) : (
                        <span>Storniert</span>
                      )}
                    </article>
                  ))}
                </section>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default WorkPlanning;